<template>
  <Teleport to="body">
    <div 
      class="fixed z-[10000] flex flex-col gap-3 pointer-events-none"
      :class="positionClass"
    >
      <TransitionGroup 
        name="toast"
        tag="div"
        class="flex flex-col gap-3"
      >
        <div
          v-for="notification in notificationStore.activeNotifications"
          :key="notification.id"
          class="pointer-events-auto w-80 md:w-96 rounded-xl shadow-2xl border overflow-hidden transform transition-all duration-300"
          :class="[
            notificationTypeClass(notification.type),
            'bg-casino-card'
          ]"
        >
          <!-- Progress bar for auto-dismiss -->
          <div 
            v-if="!notification.persistent"
            class="h-1 transition-all duration-100"
            :class="progressBarClass(notification.type)"
            :style="{ width: getProgressWidth(notification) + '%' }"
          ></div>

          <div class="p-4">
            <div class="flex items-start gap-3">
              <!-- Icon -->
              <div 
                v-if="notification.icon || typeIcon(notification.type)"
                class="w-8 h-8 rounded-lg flex items-center justify-center text-lg flex-shrink-0"
                :class="iconBgClass(notification.type)"
              >
                {{ notification.icon || typeIcon(notification.type) }}
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-2">
                  <h4 
                    v-if="notification.title" 
                    class="font-semibold text-sm text-white"
                  >
                    {{ notification.title }}
                  </h4>
                  <button
                    @click="notificationStore.removeNotification(notification.id)"
                    class="text-gray-500 hover:text-white transition-colors flex-shrink-0"
                  >
                    <X class="w-4 h-4" />
                  </button>
                </div>
                
                <p 
                  v-if="notification.message" 
                  class="text-sm text-gray-400 mt-1 break-words"
                >
                  {{ notification.message }}
                </p>

                <!-- Action Button -->
                <div v-if="notification.action" class="mt-3">
                  <button
                    @click="handleAction(notification)"
                    class="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                    :class="actionButtonClass(notification.type)"
                  >
                    {{ notification.action.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useNotificationStore } from '@/stores/notifications'
import { X } from 'lucide-vue-next'

const props = defineProps({
  position: {
    type: String,
    default: 'top-right',
    validator: (value) => [
      'top-left', 'top-center', 'top-right',
      'bottom-left', 'bottom-center', 'bottom-right'
    ].includes(value)
  }
})

const notificationStore = useNotificationStore()
const progressTimers = ref(new Map())

const positionClass = computed(() => {
  const positions = {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4'
  }
  return positions[props.position] || positions['top-right']
})

const notificationTypeClass = (type) => {
  const classes = {
    success: 'border-casino-win/30 shadow-casino-win/10',
    error: 'border-casino-loss/30 shadow-casino-loss/10',
    warning: 'border-yellow-500/30 shadow-yellow-500/10',
    info: 'border-casino-primary/30 shadow-casino-primary/10'
  }
  return classes[type] || classes.info
}

const progressBarClass = (type) => {
  const classes = {
    success: 'bg-casino-win',
    error: 'bg-casino-loss',
    warning: 'bg-yellow-500',
    info: 'bg-casino-primary'
  }
  return classes[type] || classes.info
}

const iconBgClass = (type) => {
  const classes = {
    success: 'bg-casino-win/20 text-casino-win',
    error: 'bg-casino-loss/20 text-casino-loss',
    warning: 'bg-yellow-500/20 text-yellow-500',
    info: 'bg-casino-primary/20 text-casino-primary'
  }
  return classes[type] || classes.info
}

const actionButtonClass = (type) => {
  const classes = {
    success: 'bg-casino-win/20 text-casino-win hover:bg-casino-win/30',
    error: 'bg-casino-loss/20 text-casino-loss hover:bg-casino-loss/30',
    warning: 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30',
    info: 'bg-casino-primary/20 text-casino-primary hover:bg-casino-primary/30'
  }
  return classes[type] || classes.info
}

const typeIcon = (type) => {
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  }
  return icons[type] || icons.info
}

const getProgressWidth = (notification) => {
  if (notification.persistent) return 100
  
  const elapsed = Date.now() - notification.createdAt
  const remaining = Math.max(0, notification.duration - elapsed)
  return (remaining / notification.duration) * 100
}

const handleAction = (notification) => {
  if (notification.action && notification.action.callback) {
    notification.action.callback()
  }
  notificationStore.removeNotification(notification.id)
}

// Demo notifications on mount (remove in production)
onMounted(() => {
  // Uncomment to test notifications:
  // setTimeout(() => {
  //   notificationStore.success('Welcome to Casino Demo!')
  // }, 1000)
})
</script>

<style scoped>
/* Toast animations */
.toast-enter-active {
  transition: all 0.3s ease-out;
}

.toast-leave-active {
  transition: all 0.3s ease-in;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* For center position */
.toast-enter-from[data-position-center="true"] {
  opacity: 0;
  transform: translateY(-20px);
}

.toast-leave-to[data-position-center="true"] {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
